import type {
    DocsThemeName
} from "../../theme";
import themeJSON from "../../../../src/variants/themes/default.json";

export type AnatomyTokenKind = "space" | "size" | "radius" | "border";

export type AnatomyColorRole = "background" | "border" | "text" | "icon";

type TokenEntry = {
    value: number;
    name: string;
};

type ColorEntry = {
    alpha: number;
    name: string;
};

type ParsedColor = {
    alpha: number;
    hex: string;
};

const shapes = themeJSON.shapes;

const FULL_RADIUS = 999;

const COLOR_PREFERENCES: Record<AnatomyColorRole, Array<string>> = {
    background: [
        "content.container",
        "system.default",
        "system.container",
        "system.state",
        "content.border",
        "content"
    ],
    border: [
        "content.border",
        "system.outline",
        "system.default",
        "content"
    ],
    icon: [
        "content.icon",
        "content.text",
        "system.on",
        "system.default"
    ],
    text: [
        "content.text",
        "system.on",
        "content.icon",
        "content"
    ]
};

const toEntries = (prefix: string, values: Record<string, number>): Array<TokenEntry> => Object.entries(values).map(([
    key,
    value
]) => ({
    name: `${prefix}.${key}`,
    value
}));

const TOKENS: Record<AnatomyTokenKind, Array<TokenEntry>> = {
    radius: toEntries("radiuses", shapes.radiuses.soft).filter((entry) => !entry.name.includes("scrollBar") && entry.value > 0 && entry.value < FULL_RADIUS),
    border: toEntries("borders", shapes.borders).filter((entry) => !entry.name.includes("scrollBar")),
    space: [
        ...toEntries("spaces", shapes.spaces.spacious),
        ...toEntries("controlPaddings", shapes.controlPaddings.spacious)
    ],
    size: [
        ...toEntries("controlHeights", shapes.controlHeights.spacious),
        ...toEntries("iconSizes", shapes.iconSizes),
        ...toEntries("touchTargets", shapes.touchTargets),
        ...toEntries("avatarSizes", shapes.avatarSizes)
    ]
};

export const isFullRadius = (radius: number, width: number, height: number) => radius >= FULL_RADIUS || radius >= Math.min(width, height) / 2 - 0.01;

export const findSizeTokens = (kind: AnatomyTokenKind, value: number): Array<string> => {
    const rounded = Math.round(value * 100) / 100;

    if(rounded <= 0) {
        return [];
    }

    return TOKENS[kind].filter((entry) => Math.abs(entry.value - rounded) < 0.01).map((entry) => entry.name.replace(/^spaces\./, ""));
};

export const formatTokenNames = (names: Array<string>) => {
    if(names.length <= 2) {
        return names.join(" / ");
    }

    return `${names.slice(0, 2).join(" / ")} +${names.length - 2}`;
};

const toHex = (channels: Array<string>) => `#${channels.map((channel) => Number(channel).toString(16).padStart(2, "0")).join("")}`;

const parseColor = (value: string): ParsedColor | null => {
    const color = value.trim().toLowerCase();

    const hexMatch = color.match(/^#([0-9a-f]{6})([0-9a-f]{2})?$/);

    if(hexMatch) {
        return {
            alpha: hexMatch[2] ? parseInt(hexMatch[2], 16) / 255 : 1,
            hex: `#${hexMatch[1]}`
        };
    }

    const rgbMatch = color.match(/^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)(?:[\s,/]+([\d.]+))?\s*\)$/);

    if(!rgbMatch) {
        return null;
    }

    return {
        alpha: rgbMatch[4] === undefined ? 1 : Number(rgbMatch[4]),
        hex: toHex([
            rgbMatch[1] ?? "0",
            rgbMatch[2] ?? "0",
            rgbMatch[3] ?? "0"
        ])
    };
};

const collectColors = (value: unknown, path: Array<string>, out: Map<string, Array<ColorEntry>>) => {
    if(typeof value === "string") {
        const color = parseColor(value);

        if(color) {
            const entries = out.get(color.hex) ?? [];

            entries.push({
                name: path.join("."),
                alpha: color.alpha
            });

            out.set(color.hex, entries);
        }

        return;
    }

    if(value && typeof value === "object") {
        Object.entries(value).forEach(([
            key,
            child
        ]) => collectColors(child, [
            ...path,
            key
        ], out));
    }
};

const COLOR_CACHE = new Map<DocsThemeName, Map<string, Array<ColorEntry>>>();

const getColorMap = (themeName: DocsThemeName) => {
    const cached = COLOR_CACHE.get(themeName);

    if(cached) {
        return cached;
    }

    const palette = themeJSON.palettes.find((item) => item.name === "nibgat");

    const map = new Map<string, Array<ColorEntry>>();

    if(palette) {
        collectColors(palette.themes[themeName].content, [
            "content"
        ], map);

        collectColors(palette.themes[themeName].system, [
            "system"
        ], map);
    }

    COLOR_CACHE.set(themeName, map);

    return map;
};

const getPreferenceIndex = (name: string, preferences: Array<string>) => {
    const index = preferences.findIndex((prefix) => name.startsWith(prefix));

    return index === -1 ? preferences.length : index;
};

const getColorScore = (name: string, preferences: Array<string>, pairedName?: string) => {
    const pairedSuffix = pairedName ? `.on${pairedName.charAt(0).toUpperCase()}${pairedName.slice(1)}` : null;

    return getPreferenceIndex(name, preferences) * 2 + (pairedSuffix && name.endsWith(pairedSuffix) ? 0 : 1);
};

export const findColorToken = (cssColor: string, themeName: DocsThemeName, role: AnatomyColorRole, pairedName?: string) => {
    const color = parseColor(cssColor);

    if(!color || color.alpha === 0) {
        return null;
    }

    const preferences = COLOR_PREFERENCES[role];

    const matches = (getColorMap(themeName).get(color.hex) ?? []).filter((entry) => Math.abs(entry.alpha - color.alpha) < 0.01);

    const sorted = [...matches].sort((first, second) => getColorScore(first.name, preferences, pairedName) - getColorScore(second.name, preferences, pairedName));

    return {
        name: sorted[0]?.name ?? null,
        alpha: color.alpha,
        hex: color.hex
    };
};

const splitShadowLayers = (boxShadow: string) => {
    const layers: Array<string> = [];

    let depth = 0;

    let current = "";

    for(const char of boxShadow) {
        if(char === "(") {
            depth += 1;
        }

        if(char === ")") {
            depth -= 1;
        }

        if(char === "," && depth === 0) {
            layers.push(current);

            current = "";

            continue;
        }

        current += char;
    }

    layers.push(current);

    return layers.map((layer) => {
        const lengths = layer.replace(/rgba?\([^)]*\)/g, "").trim().split(/\s+/).filter((item) => item.endsWith("px")).map((item) => Number.parseFloat(item));

        return {
            offset: lengths[1] ?? 0,
            blur: lengths[2] ?? 0
        };
    });
};

export const findElevationToken = (boxShadow: string | null) => {
    if(!boxShadow) {
        return null;
    }

    const layers = splitShadowLayers(boxShadow);

    const match = Object.entries(shapes.elevations).find(([
        , levels
    ]) => levels.length === layers.length && levels.every((level, index) => level.offset === layers[index]?.offset && level.blur === layers[index]?.blur));

    return match ? `elevations.${match[0]}` : null;
};

export const findTypographyRole = (fontSize: number, lineHeight: number | null, fontWeight: number) => {
    const roles = Object.entries(themeJSON.typography.spacious);

    const match = roles.find(([
        , role
    ]) => role.fontSize === fontSize && (lineHeight === null || role.lineHeight === lineHeight) && Number(role.fontWeight) === fontWeight);

    return match ? match[0].replace(/Size$/, "") : null;
};
